import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { createStockBucket, createStockBucketItem, } from "../services/api";
import CatalogSearch from "./CatalogSearch";
import "./AddStockBucket.css";
export default function AddStockBucket({ onSaved, warehouses }) {
    const { instance } = useMsal();
    const [mode, setMode] = useState("bucket");
    // Shared state
    const [warehouseSearch, setWarehouseSearch] = useState("");
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [showWarehouseDropdown, setShowWarehouseDropdown] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    // Bucket mode state
    const [name, setName] = useState("");
    const [minimumStockLevel, setMinimumStockLevel] = useState("");
    const [selectedItems, setSelectedItems] = useState([]);
    // Item mode state
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemMinStock, setItemMinStock] = useState("");
    const filteredWarehouses = useMemo(() => {
        if (!warehouseSearch.trim())
            return warehouses;
        const lower = warehouseSearch.toLowerCase();
        return warehouses.filter((w) => w.name.toLowerCase().includes(lower));
    }, [warehouses, warehouseSearch]);
    const addedItemIds = useMemo(() => new Set(selectedItems.map((i) => i.id)), [selectedItems]);
    const selectedItemIds = useMemo(() => new Set(selectedItem ? [selectedItem.id] : []), [selectedItem]);
    const handleAddItem = (item) => {
        if (mode === "item") {
            setSelectedItem(item);
        }
        else {
            setSelectedItems((prev) => [...prev, item]);
        }
    };
    const handleRemoveItem = (id) => {
        setSelectedItems((prev) => prev.filter((i) => i.id !== id));
    };
    const resetForm = () => {
        setName("");
        setMinimumStockLevel("");
        setSelectedItems([]);
        setSelectedItem(null);
        setItemMinStock("");
        setSelectedWarehouse(null);
        setWarehouseSearch("");
    };
    const handleModeChange = (newMode) => {
        setMode(newMode);
        setError("");
        setSuccess("");
        resetForm();
    };
    const handleSaveBucket = async () => {
        setError("");
        setSuccess("");
        if (!name.trim()) {
            setError("Name is required.");
            return;
        }
        if (minimumStockLevel === "" || minimumStockLevel < 0) {
            setError("A valid minimum stock level is required.");
            return;
        }
        if (!selectedWarehouse) {
            setError("Please select a warehouse.");
            return;
        }
        if (selectedItems.length === 0) {
            setError("Please add at least one catalog item.");
            return;
        }
        setSaving(true);
        try {
            const bucket = await createStockBucket(instance, {
                name: name.trim(),
                minimumStockLevel: Number(minimumStockLevel),
                warehouseId: selectedWarehouse.id,
                warehouseName: selectedWarehouse.name,
            });
            for (const item of selectedItems) {
                await createStockBucketItem(instance, {
                    stockBucketId: bucket.id,
                    cwItemId: item.id,
                    manufacturerPartNumber: item.manufacturerPartNumber || item.identifier,
                    partDescription: item.description,
                    warehouseId: selectedWarehouse.id,
                    warehouseName: selectedWarehouse.name,
                });
            }
            setSuccess("Stock notification group saved successfully!");
            resetForm();
            onSaved();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to save. Please try again.");
        }
        finally {
            setSaving(false);
        }
    };
    const handleSaveItem = async () => {
        setError("");
        setSuccess("");
        if (!selectedWarehouse) {
            setError("Please select a warehouse.");
            return;
        }
        if (itemMinStock === "" || itemMinStock < 0) {
            setError("A valid minimum stock level is required.");
            return;
        }
        if (!selectedItem) {
            setError("Please search for and select a catalog item.");
            return;
        }
        setSaving(true);
        try {
            await createStockBucketItem(instance, {
                cwItemId: selectedItem.id,
                manufacturerPartNumber: selectedItem.manufacturerPartNumber || selectedItem.identifier,
                partDescription: selectedItem.description,
                warehouseId: selectedWarehouse.id,
                warehouseName: selectedWarehouse.name,
                minimumStockLevel: Number(itemMinStock),
            });
            setSuccess("Standalone item saved successfully!");
            resetForm();
            onSaved();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to save. Please try again.");
        }
        finally {
            setSaving(false);
        }
    };
    const renderWarehousePicker = () => (_jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Warehouse" }), _jsxs("div", { className: "warehouse-search", children: [_jsx("input", { type: "text", value: selectedWarehouse ? selectedWarehouse.name : warehouseSearch, onChange: (e) => {
                            setWarehouseSearch(e.target.value);
                            setSelectedWarehouse(null);
                            setShowWarehouseDropdown(true);
                        }, onFocus: () => setShowWarehouseDropdown(true), onBlur: () => setTimeout(() => setShowWarehouseDropdown(false), 200), placeholder: "Search warehouses..." }), showWarehouseDropdown && filteredWarehouses.length > 0 && (_jsx("div", { className: "warehouse-dropdown", children: filteredWarehouses.map((w) => (_jsx("div", { className: "wh-option", onMouseDown: () => {
                                setSelectedWarehouse(w);
                                setWarehouseSearch(w.name);
                                setShowWarehouseDropdown(false);
                            }, children: w.name }, w.id))) }))] })] }));
    return (_jsxs("div", { className: "add-stock-bucket", children: [_jsx("h2", { children: "Add New Entry" }), _jsxs("div", { className: "mode-toggle", children: [_jsxs("label", { className: `mode-option ${mode === "bucket" ? "active" : ""}`, children: [_jsx("input", { type: "radio", name: "entryMode", value: "bucket", checked: mode === "bucket", onChange: () => handleModeChange("bucket") }), "Notification Group (Bucket)"] }), _jsxs("label", { className: `mode-option ${mode === "item" ? "active" : ""}`, children: [_jsx("input", { type: "radio", name: "entryMode", value: "item", checked: mode === "item", onChange: () => handleModeChange("item") }), "Standalone Item"] })] }), mode === "bucket" ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "form-row", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Bucket Name" }), _jsx("input", { type: "text", value: name, onChange: (e) => setName(e.target.value), placeholder: "Enter bucket name" })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Minimum Stock Level" }), _jsx("input", { type: "number", min: "0", value: minimumStockLevel, onChange: (e) => setMinimumStockLevel(e.target.value === "" ? "" : Number(e.target.value)), placeholder: "e.g. 10" })] }), renderWarehousePicker()] }), _jsx("h3", { style: { margin: "16px 0 4px", fontSize: "0.95rem" }, children: "Search Catalog Items" }), _jsx(CatalogSearch, { onAddItem: handleAddItem, addedItemIds: addedItemIds }), selectedItems.length > 0 && (_jsxs("div", { className: "selected-items", children: [_jsxs("h3", { children: ["Selected Items (", selectedItems.length, ")"] }), _jsxs("table", { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Identifier" }), _jsx("th", { children: "Description" }), _jsx("th", { children: "Mfr Part #" }), _jsx("th", {})] }) }), _jsx("tbody", { children: selectedItems.map((item) => (_jsxs("tr", { children: [_jsx("td", { children: item.identifier }), _jsx("td", { children: item.description }), _jsx("td", { children: item.manufacturerPartNumber }), _jsx("td", { children: _jsx("button", { className: "btn-remove", onClick: () => handleRemoveItem(item.id), children: "Remove" }) })] }, item.id))) })] })] }))] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "form-row", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Minimum Stock Level" }), _jsx("input", { type: "number", min: "0", value: itemMinStock, onChange: (e) => setItemMinStock(e.target.value === "" ? "" : Number(e.target.value)), placeholder: "e.g. 10" })] }), renderWarehousePicker()] }), _jsx("h3", { style: { margin: "16px 0 4px", fontSize: "0.95rem" }, children: "Search for a Catalog Item" }), _jsx(CatalogSearch, { onAddItem: handleAddItem, addedItemIds: selectedItemIds }), selectedItem && (_jsxs("div", { className: "selected-items", children: [_jsx("h3", { children: "Selected Item" }), _jsxs("table", { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Identifier" }), _jsx("th", { children: "Description" }), _jsx("th", { children: "Mfr Part #" }), _jsx("th", {})] }) }), _jsx("tbody", { children: _jsxs("tr", { children: [_jsx("td", { children: selectedItem.identifier }), _jsx("td", { children: selectedItem.description }), _jsx("td", { children: selectedItem.manufacturerPartNumber }), _jsx("td", { children: _jsx("button", { className: "btn-remove", onClick: () => setSelectedItem(null), children: "Remove" }) })] }) })] })] }))] })), error && _jsx("p", { className: "error", children: error }), success && _jsx("p", { className: "success", children: success }), _jsx("button", { className: "btn-save", onClick: mode === "bucket" ? handleSaveBucket : handleSaveItem, disabled: saving, children: saving
                    ? "Saving..."
                    : mode === "bucket"
                        ? "Save Notification Group"
                        : "Save Standalone Item" })] }));
}
